/**
 * Trattative sul telefono (7/10/2026): risponde a "chi devo seguire?".
 *
 * Prima, a 390px: quattro riquadri di periodi (spesso a € 0) in cima, poi
 * schede alte quasi uno schermo con PIPELINE / STEP / VALORE / PREVISTA /
 * PROSSIMA AZIONE: si vedeva una trattativa e mezza per volta.
 * Qui:
 *   1. in grande, quanto vale quello che stiamo vendendo e quante sono;
 *   2. le viste (in corso, ferme da richiamare, vinte, chiuse) e la ricerca;
 *   3. le trattative raggruppate per step, una riga ciascuna: chi è, la
 *      prossima azione (rossa se in ritardo), il valore. Il resto è nella
 *      scheda, che si apre toccando la riga.
 * Stesse chiamate e stessi parametri nell'indirizzo della pagina del computer
 * (view, q, stale, pipeline): un link copiato da una funziona nell'altra.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fmtEur, euro } from '../../lib/utils';
import { useUrlState } from '../../hooks/useUrlState';
import { MEmpty, MGroup, MHero, MPage, MRow, MSearch, MSeg, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { counterparty, dealsListQuery, dealsSummaryQuery, dueInfo, type DealItem, type DealView } from './dealsApi';

type Vista = 'aperte' | 'ferme' | 'vinte' | 'chiuse';

/** Valori lunghi in poco spazio: € 40.000 resta così, € 1.250.000 diventa € 1,25 mln. */
function valore(n: number): string {
  if (n >= 1_000_000) return `€ ${(n / 1_000_000).toLocaleString('it-IT', { maximumFractionDigits: 2 })} mln`;
  return `${euro(n)}`;
}

export function TrattativeMobile() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [viewParam] = useUrlState<string>('view', 'aperte');
  const [staleParam] = useUrlState<string>('stale', '');
  const [pipelineParam] = useUrlState<string>('pipeline', '');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });

  const vista: Vista = staleParam === '1' ? 'ferme' : (['vinte', 'chiuse'].includes(viewParam) ? viewParam : 'aperte') as Vista;
  const view: DealView = vista === 'ferme' ? 'aperte' : vista;

  const { data: summary } = useQuery(dealsSummaryQuery(pipelineParam || undefined));
  const { data: lista, isLoading } = useQuery(dealsListQuery({
    view,
    pipeline: pipelineParam || undefined,
    search: search.trim() || undefined,
    stale: vista === 'ferme' || undefined,
    sort: 'chiusura',
    page: 1,
    pageSize: 300,
  }));

  const aperte = (summary?.byStage ?? []).filter((s) => s.outcome === 'APERTO');
  const valoreAperte = aperte.reduce((n, s) => n + s.amount, 0);
  const contaAperte = aperte.reduce((n, s) => n + s.count, 0);
  const conta = (outcome: string) => (summary?.byStage ?? []).filter((s) => s.outcome === outcome).reduce((n, s) => n + s.count, 0);

  const scegli = (k: Vista) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('stale');
      if (k === 'ferme') { sp.delete('view'); sp.set('stale', '1'); }
      else if (k === 'aperte') sp.delete('view');
      else sp.set('view', k);
      return sp;
    }, { replace: true });
  };

  // In corso e ferme: raggruppate per step, nell'ordine della pipeline. Vinte e chiuse: una lista sola.
  const gruppi = useMemo(() => {
    const items = lista?.items ?? [];
    if (vista === 'vinte' || vista === 'chiuse') return [{ chiave: vista, titolo: vista === 'vinte' ? 'Vinte' : 'Chiuse senza accordo', items }];
    const perStep = new Map<string, { chiave: string; titolo: string; ordine: number; items: DealItem[] }>();
    for (const d of items) {
      const k = `${d.pipeline?.name ?? ''}|${d.stage.name}`;
      if (!perStep.has(k)) perStep.set(k, { chiave: k, titolo: d.stage.name, ordine: d.stage.order, items: [] });
      perStep.get(k)!.items.push(d);
    }
    return [...perStep.values()].sort((a, b) => a.ordine - b.ordine);
  }, [lista, vista]);

  const staleDays = summary?.staleDays ?? 14;
  const riga = (d: DealItem) => {
    const nome = leggibile(counterparty(d));
    const t = d.nextTask;
    const scad = t?.dueDate ? dueInfo(t.dueDate) : null;
    // Senza una prossima azione, la cosa utile da sapere è da quanto non la
    // sentiamo: rosso solo oltre la soglia delle "ferme". Scritto "nessuna
    // prossima azione" in rosso su tutte, la lista era una parete d'allarme.
    const giorni = d.daysSinceLastEvent;
    const silenzio = giorni <= 0 ? 'sentita oggi' : giorni === 1 ? 'sentita ieri' : `non la sentiamo da ${giorni} giorni`;
    const prossima = t
      ? <><span className={t.isOverdue ? 'is-alert' : 'is-ink'}>{scad && scad.label !== '—' ? `${scad.label}: ` : ''}</span>{t.title}</>
      : vista === 'vinte' || vista === 'chiuse'
        ? [d.stage.name, d.lostReason].filter(Boolean).join(' · ')
        // 26/10/2026: anche "oltre la soglia" era rosso su quasi tutte le righe
        // (dopo l'aggiornamento di ottobre sono quasi tutte ferme). Il rosso
        // resta nella frase in alto, che le conta; qui il silenzio si legge e basta.
        : <span>{silenzio}</span>;
    return (
      <MRow
        key={d.id}
        text={iniziali(nome) || '·'}
        round
        title={nome}
        sub={prossima}
        trail={d.value > 0 ? valore(d.value) : <span style={{ color: 'var(--mute-2)', fontWeight: 500 }}>senza valore</span>}
        onClick={() => navigate(`/trattative/${d.id}`)}
      />
    );
  };

  return (
    <MPage>
      <MHero
        label={`${contaAperte} ${contaAperte === 1 ? 'trattativa in corso' : 'trattative in corso'}`}
        unit="€"
        value={fmtEur(valoreAperte)}
        sub={(summary?.stale ?? 0) > 0
          ? <span className="is-alert">{summary!.stale} ferme da oltre {summary!.staleDays} giorni</span>
          : 'nessuna ferma da richiamare'}
      />

      {/* minmax(0,1fr): senza, a 360px la colonna si allarga quanto le pillole e sborda di 2px */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--s-3)' }}>
        <MSeg<Vista>
          value={vista}
          onChange={scegli}
          options={[
            { key: 'aperte', label: 'In corso', count: summary ? contaAperte : undefined },
            { key: 'ferme', label: 'Da richiamare', count: summary?.stale },
            { key: 'vinte', label: 'Vinte', count: summary ? conta('VINTO') : undefined },
            { key: 'chiuse', label: 'Chiuse', count: summary ? conta('PERSO') : undefined },
          ]}
        />
        <MSearch value={search} onChange={setSearch} placeholder="Cerca un'azienda o una persona" />
      </div>

      {isLoading && !lista ? (
        <MGroup title="Trattative"><MEmpty>Carico le trattative…</MEmpty></MGroup>
      ) : gruppi.length === 0 || gruppi.every((g) => g.items.length === 0) ? (
        <MGroup title="Trattative">
          <MEmpty>{search.trim() ? `Nessuna trattativa con "${search.trim()}".` : vista === 'ferme' ? 'Nessuna trattativa ferma: le state seguendo tutte.' : 'Nessuna trattativa qui.'}</MEmpty>
        </MGroup>
      ) : (
        gruppi.map((g) => (
          <MGroup key={g.chiave} title={g.titolo} count={g.items.length}>
            {g.items.map(riga)}
          </MGroup>
        ))
      )}
    </MPage>
  );
}
